import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {createStyles} from '@mantine/core';
import React, {useMemo} from 'react';

const CELL_SPACING = 6;
const WELL_RADIUS = 2;
const STROKE_WIDTH = 0.7;
const DEFAULT_SIZE_PX = 64;

export const MINI_PLATE_SUMMARY_FILLED_WELL_COLOR = Color.GRAY_500;
export const MINI_PLATE_SUMMARY_EMPTY_WELL_STROKE = Color.GRAY_300;

export interface MiniPlateSummaryFilledPosition {
  rowIndex: number;
  columnIndex: number;
}

interface MiniPlateSummaryProps {
  width: number;
  height: number;
  filledPositions: ReadonlyArray<MiniPlateSummaryFilledPosition>;
  sizePx?: number;
}

function getWellPositionKey(rowIndex: number, columnIndex: number): string {
  return `${rowIndex}-${columnIndex}`;
}

export default function MiniPlateSummary({
  width,
  height,
  filledPositions,
  sizePx = DEFAULT_SIZE_PX,
}: MiniPlateSummaryProps): JSX.Element {
  const {classes} = useStyles();
  const filledPositionSet = useMemo(
    () =>
      new Set(filledPositions.map(({rowIndex, columnIndex}) => getWellPositionKey(rowIndex, columnIndex))),
    [filledPositions]
  );
  const rowCount = Math.max(height, 1);
  const columnCount = Math.max(width, 1);
  const viewBoxWidth = columnCount * CELL_SPACING;
  const viewBoxHeight = rowCount * CELL_SPACING;
  const renderedHeight = (sizePx * viewBoxHeight) / viewBoxWidth;

  return (
    <svg
      {...getTestProps({component: 'MiniPlateSummary', element: 'root'})}
      aria-hidden={true}
      className={classes.root}
      focusable={false}
      height={renderedHeight}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      width={sizePx}
    >
      {Array.from({length: rowCount}).flatMap((_, rowIndex) =>
        Array.from({length: columnCount}).map((__, columnIndex) => {
          const key = getWellPositionKey(rowIndex, columnIndex);
          const isFilled = filledPositionSet.has(key);
          return (
            <circle
              {...getTestProps({component: 'MiniPlateSummary', element: 'well', key})}
              key={key}
              cx={columnIndex * CELL_SPACING + CELL_SPACING / 2}
              cy={rowIndex * CELL_SPACING + CELL_SPACING / 2}
              fill={isFilled ? MINI_PLATE_SUMMARY_FILLED_WELL_COLOR : Color.WHITE}
              r={WELL_RADIUS}
              stroke={isFilled ? 'none' : MINI_PLATE_SUMMARY_EMPTY_WELL_STROKE}
              strokeWidth={isFilled ? 0 : STROKE_WIDTH}
              data-amp-safe={false}
            />
          );
        })
      )}
    </svg>
  );
}

const useStyles = createStyles(() => ({
  root: {
    display: 'block',
    flex: '0 0 auto',
  },
}));
